import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import {
  GlobalStyle,
  MainContainer,
  SideContainer,
  BackgroundPattern,
  ScanlineEffect,
  ContentWrapper,
  HeaderSection,
  Title,
  Subtitle,
  MenuGrid,
  ActiveButton,
  ActionButton,
  ButtonIcon,
  ButtonText,
  ButtonDescription,
  Panel,
  PanelTitle,
  EditorArea,
  WorkflowButtons,
  WorkflowBtn,
  StepBadge,
  ConsoleBox,
  ConsoleLine,
  Cursor,
  CodePanel,
  CodeBlock,
  ChipsRow,
  Chip,
  ConfigCard,
  ConfigRow,
  ConfigLabel,
  ConfigValue,
  LockIcon,
  SectionLabel,
  NetworkSelector,
  NetworkBtn,
  AccountPill,
  AccountDot,
  AccountText,
} from "./styles.js";
import Sidebar from "../../components/sidebar/index.jsx";
import { compileContracts } from "../../services/contractCompiler.js";

// ── Código-fonte dos contratos auxiliares (mantido igual) ─────────────────────
const CONTRATO_PROJETO_SOURCE = `// SPDX-License-Identifier: GPL-3.0
pragma solidity ^0.8.24;
import { Ownable } from "@openzeppelin/contracts/access/Ownable.sol";
import { contrato_projeto_level1 } from "./contrato_projeto_level1.sol";
import { SignatureRecovery } from "./SignatureRecovery.sol";

contract Factorylevel1 is Ownable {
    struct ContratoInfo {
        address enderecoContrato;
        string nomePropriedade;
        string nomeProprietario;
        string cnpjcpf;
        string[] car;
        string file_manager_contract_id;
    }

    address[] public ProjectID;
    mapping(address => ContratoInfo) public contratoInfo;

    event NovoContrato (address contratoAddress, address criador, string nomePropriedade, string nomeProprietario, string cnpjcpf, string[] car, string file_manager_contract_id, address signer1, address owner1);

    SignatureRecovery private signatureRecovery;

    constructor() Ownable(msg.sender) {
        signatureRecovery = new SignatureRecovery();
    }

    function Factory(
        string memory nomePropriedade,
        string memory nomeProprietario,
        string memory cnpjcpf,
        string[] memory car,
        string memory file_manager_contract_id,
        bytes memory signature
    ) external onlyOwner {
        require(car.length > 0, "Pelo menos um codigo CAR e necessario");
        for (uint256 i = 0; i < car.length; i++) {
            require(bytes(car[i]).length > 0, "Codigo CAR nao pode estar vazio");
        }

        bytes memory carBytes = abi.encode(car);
        bytes32 messageHash = keccak256(
            abi.encode(nomePropriedade, nomeProprietario, cnpjcpf, carBytes, file_manager_contract_id)
        );
        address signer = signatureRecovery.recoverSigner(messageHash, signature);
        require(signer == owner(), "Assinatura invalida");

        contrato_projeto_level1 newContract = new contrato_projeto_level1(msg.sender);
        address addr = address(newContract);
        ProjectID.push(addr);

        contratoInfo[addr] = ContratoInfo({
            enderecoContrato: addr,
            nomePropriedade: nomePropriedade,
            nomeProprietario: nomeProprietario,
            cnpjcpf: cnpjcpf,
            car: car,
            file_manager_contract_id: file_manager_contract_id
        });

        emit NovoContrato(addr, msg.sender, nomePropriedade, nomeProprietario, cnpjcpf, car, file_manager_contract_id, signer, owner());
    }
}`;

const SIGNATURE_RECOVERY_SOURCE = `// SPDX-License-Identifier: GPL-3.0
pragma solidity ^0.8.24;
import "@openzeppelin/contracts/utils/cryptography/ECDSA.sol";
import "@openzeppelin/contracts/utils/cryptography/MessageHashUtils.sol";

contract SignatureRecovery {
    using ECDSA for bytes32;

    function validaAssinatura(bytes32 messageHash, bytes memory signature, address owner)
        public pure returns (bool)
    {
        return recoverSigner(messageHash, signature) == owner;
    }

    function recoverSigner(bytes32 messageHash, bytes memory signature)
        public pure returns (address)
    {
        bytes32 ethSigned = MessageHashUtils.toEthSignedMessageHash(messageHash);
        return ECDSA.recover(ethSigned, signature);
    }
}`;

const CONTRATO_PROJETO_LEVEL1_SOURCE = `// SPDX-License-Identifier: GPL-3.0
pragma solidity ^0.8.24;
import "@openzeppelin/contracts/token/ERC721/ERC721.sol";
import "@openzeppelin/contracts/access/Ownable.sol";
import { SignatureRecovery } from "./SignatureRecovery.sol";

contract contrato_projeto_level1 is ERC721, Ownable {
    uint256 private _tokenIdCounter;
    SignatureRecovery private signatureRecovery;

    constructor(address initialOwner)
        ERC721("level1", "lvl1")
        Ownable(initialOwner)
    {
        signatureRecovery = new SignatureRecovery();
    }

    function totalSupply() public view returns (uint256) {
        return _tokenIdCounter;
    }
}`;

// ── Configurações fixas de deploy ─────────────────────────────────────────────
const DEPLOY_CONFIG = {
  compilerVersion: "0.8.24",
  evmVersion: "cancun",
  optimizer: { enabled: true, runs: 200 },
  contractName: "Factorylevel1",
};

// ── Redes disponíveis ─────────────────────────────────────────────────────────
const NETWORKS = [
  { id: "sepolia", label: "Sepolia Testnet", chainId: "0xaa36a7", rpcUrl: "https://rpc.sepolia.org", symbol: "ETH", color: "#006693" },
  { id: "mumbai", label: "Polygon Mumbai", chainId: "0x13881", rpcUrl: "https://rpc-mumbai.maticvigil.com", symbol: "MATIC", color: "#8247e5" },
  { id: "mainnet", label: "Ethereum Mainnet", chainId: "0x1", rpcUrl: "https://cloudflare-eth.com", symbol: "ETH", color: "#00b056" },
  // 👇 NOVA REDE
  {
    id: "hardhat-aws",
    label: "Hardhat AWS",
    chainId: "0x7A69",        // 31337 em hex
    rpcUrl: "http://54.94.121.89:8545",
    symbol: "ETH",
    color: "#f15a24"
  },
];

const VIEWS = {
  editor: "editor",
  workflow: "workflow",
  console: "console",
  docs: "docs",
};

// ── Utilitários ───────────────────────────────────────────────────────────────
const shortAddr = (addr) =>
  addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : null;

// ── Componente principal ──────────────────────────────────────────────────────
export default function MiniRemixClone() {
  const [activeView, setActiveView] = useState(VIEWS.editor);
  const [code, setCode] = useState(CONTRATO_PROJETO_SOURCE);
  const [logs, setLogs] = useState([
    { text: `solc-js v${DEPLOY_CONFIG.compilerVersion} inicializado`, type: "success" },
    { text: `EVM target: ${DEPLOY_CONFIG.evmVersion} · optimizer: ${DEPLOY_CONFIG.optimizer.runs} runs`, type: "muted" },
    { text: "Serviço de compilação via Django API (autenticado)", type: "muted" },
    { text: "Aguardando ações…", type: "muted" },
  ]);
  const [account, setAccount] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState(NETWORKS[0].id);
  const [factoryAddress, setFactoryAddress] = useState("");
  const [compiledAbi, setCompiledAbi] = useState(null);
  const [compiledBytecode, setCompiledBytecode] = useState(null);
  const [isCompiling, setIsCompiling] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [accountsList, setAccountsList] = useState([]);
  const [showAccountSelector, setShowAccountSelector] = useState(false);

  const addLog = (text, type = "muted") =>
    setLogs(prev => [...prev, { text, type }]);

  useEffect(() => {
    if (window.ethereum) {
      const handleChainChanged = (chainId) => {
        const hexChainId = parseInt(chainId, 16).toString(16);
        const network = NETWORKS.find(n => n.chainId === `0x${hexChainId}`);
        if (network) setSelectedNetwork(network.id);
        addLog(`Rede alterada manualmente para ${network?.label || chainId}`, "muted");
      };
      window.ethereum.on('chainChanged', handleChainChanged);
      return () => window.ethereum.removeListener('chainChanged', handleChainChanged);
    }
  }, []);

  const fetchAllAccounts = async () => {
    if (!window.ethereum) return [];
    try {
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      return accounts;
    } catch (err) {
      console.error("Erro ao buscar contas:", err);
      return [];
    }
  };

  const requestAdditionalAccount = async () => {
    try {
      await window.ethereum.request({
        method: 'wallet_requestPermissions',
        params: [{ eth_accounts: {} }]
      });
      // Aguarda um pouco e atualiza a lista
      const accounts = await fetchAllAccounts();
      setAccountsList(accounts);
      if (accounts.length > 0) {
        setShowAccountSelector(true);
        addLog("Nova conta autorizada. Selecione-a abaixo.", "success");
      } else {
        addLog("Nenhuma conta adicional foi autorizada.", "muted");
      }
    } catch (err) {
      addLog(`Erro ao adicionar conta: ${err.message}`, "error");
    }
  };

  // ── 01 · Compilar via serviço ──────────────────────────────────────────────
  const handleCompile = async () => {
    setIsCompiling(true);
    addLog("Iniciando compilação via Django API…", "muted");
    addLog("Usando serviço de compilação autenticado", "muted");
    addLog(
      `Compilador: solc ${DEPLOY_CONFIG.compilerVersion} · EVM: ${DEPLOY_CONFIG.evmVersion} · optimizer: ${DEPLOY_CONFIG.optimizer.runs} runs`,
      "muted"
    );

    try {
      const sources = {
        "Factorylevel1.sol": code,
        "SignatureRecovery.sol": SIGNATURE_RECOVERY_SOURCE,
        "contrato_projeto_level1.sol": CONTRATO_PROJETO_LEVEL1_SOURCE,
      };

      const data = await compileContracts(sources, DEPLOY_CONFIG.contractName);

      const abi = data.abi;
      const bytecode = data.bytecode;

      if (!abi || !bytecode) {
        throw new Error("Resposta da API não contém 'abi' ou 'bytecode'.");
      }

      setCompiledAbi(abi);
      setCompiledBytecode(bytecode);

      addLog("ABI gerado com sucesso ✓", "success");
      addLog("Bytecode gerado com sucesso ✓", "success");
      addLog(`Contrato ${DEPLOY_CONFIG.contractName} pronto para deploy ✓`, "success");

      if (data.warnings?.length) {
        data.warnings.forEach(w => addLog(`⚠ ${w}`, "muted"));
      }
    } catch (err) {
      addLog(`Erro na compilação: ${err.message}`, "error");
      console.error("[Compile]", err);
    } finally {
      setIsCompiling(false);
    }
  };

  // ── 02 · Conectar MetaMask ──────────────────────────────────────────────────
  const switchOrAddNetwork = async (network) => {
    try {
      // Tenta apenas trocar
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: network.chainId }],
      });
      addLog(`Rede alterada: ${network.label} ✓`, "success");
    } catch (switchError) {
      // Se a rede não existir no MetaMask, código de erro 4902
      if (switchError.code === 4902) {
        addLog(`Rede ${network.label} não encontrada. Adicionando...`, "muted");
        try {
          await window.ethereum.request({
            method: "wallet_addEthereumChain",
            params: [
              {
                chainId: network.chainId,
                chainName: network.label,
                nativeCurrency: { name: network.symbol, symbol: network.symbol, decimals: 18 },
                rpcUrls: [network.rpcUrl],
                blockExplorerUrls: [], // vazio pois não tem explorer
              },
            ],
          });
          addLog(`Rede ${network.label} adicionada e ativada ✓`, "success");
        } catch (addError) {
          throw new Error(`Não foi possível adicionar a rede: ${addError.message}`);
        }
      } else {
        throw switchError;
      }
    }
  };

  const handleConnect = async () => {
    addLog("Conectando MetaMask…", "muted");
    try {
      if (!window.ethereum) throw new Error("MetaMask não encontrada.");
      await window.ethereum.request({ method: "eth_requestAccounts" });
      const allAccounts = await fetchAllAccounts();
      setAccountsList(allAccounts);

      if (allAccounts.length > 1) {
        setShowAccountSelector(true);
        addLog(`Encontradas ${allAccounts.length} contas. Selecione uma.`, "muted");
      } else if (allAccounts.length === 1) {
        // Se for a conta que você não quer, mostramos o seletor mesmo assim com botão de adicionar
        setAccount(allAccounts[0]);
        addLog(`Conta conectada: ${shortAddr(allAccounts[0])} ✓`, "success");
        // Mesmo com uma conta, mostramos o seletor para permitir adicionar outra
        setShowAccountSelector(true);
        const net = NETWORKS.find(n => n.id === selectedNetwork);
        if (net) await switchOrAddNetwork(net);
      } else {
        addLog("Nenhuma conta encontrada.", "error");
      }
    } catch (e) {
      addLog(`Erro ao conectar: ${e.message}`, "error");
    }
  };

  const handleSwitchAccount = async () => {
    const allAccounts = await fetchAllAccounts();
    if (allAccounts.length > 0) {
      setAccountsList(allAccounts);
      setShowAccountSelector(true);
    } else {
      addLog("Nenhuma conta disponível.", "error");
    }
  };

  const handleSelectAccount = async (selected) => {
    setAccount(selected);
    setShowAccountSelector(false);
    addLog(`Conta selecionada: ${shortAddr(selected)} ✓`, "success");

    const net = NETWORKS.find(n => n.id === selectedNetwork);
    if (net) {
      await switchOrAddNetwork(net);
    }
  };

  // ── 03 · Deploy ─────────────────────────────────────────────────────────────
  const handleDeploy = async () => {
    if (!account) {
      addLog("Erro: selecione uma conta antes de fazer deploy.", "error");
      return;
    }
    if (!compiledAbi || !compiledBytecode) {
      addLog("Erro: compile o contrato antes de fazer deploy.", "error");
      return;
    }

    setIsDeploying(true);
    addLog(`Iniciando deploy de: ${DEPLOY_CONFIG.contractName}`, "muted");
    addLog(`Usando conta: ${shortAddr(account)}`, "muted");
    addLog("Aguardando confirmação na MetaMask…", "muted");

    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner(account); // ← usa a conta selecionada
      const factory = new ethers.ContractFactory(compiledAbi, compiledBytecode, signer);
      const contract = await factory.deploy();
      addLog(`Tx enviada: ${shortAddr(contract.deploymentTransaction()?.hash)} — aguardando mineração…`, "muted");

      await contract.waitForDeployment();
      const address = await contract.getAddress();
      setFactoryAddress(address);
      addLog(`✅ Contrato implantado em: ${address}`, "addr");
    } catch (err) {
      addLog(`Erro no deploy: ${err.message}`, "error");
      console.error("[Deploy]", err);
    } finally {
      setIsDeploying(false);
    }
  };

  // ── 04 · Signature Recovery ─────────────────────────────────────────────────
  const handleSigRecovery = async () => {
    if (!account) {
      addLog("Erro: selecione uma conta primeiro.", "error");
      return;
    }
    addLog(`Executando signature recovery com a conta ${shortAddr(account)}…`, "muted");
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner(account); // ← conta selecionada
      const msg = "Verificação de assinatura — Factorylevel1";
      const signature = await signer.signMessage(msg);
      const recovered = ethers.verifyMessage(msg, signature);
      addLog(`Signer recuperado: ${shortAddr(recovered)} ✓`, "success");
      if (recovered.toLowerCase() === account.toLowerCase()) {
        addLog("Assinatura válida — corresponde à conta selecionada ✓", "success");
      } else {
        addLog("⚠ Signer não corresponde à conta selecionada!", "error");
      }
    } catch (e) {
      addLog(`Erro: ${e.message}`, "error");
    }
  };

  const menuItems = [
    { id: VIEWS.editor, icon: "✏️", label: "Editor", desc: "Editar código Solidity" },
    { id: VIEWS.workflow, icon: "⚙️", label: "Workflow", desc: "Compilar e fazer deploy" },
    { id: VIEWS.console, icon: "🖥️", label: "Console", desc: "Output e logs em tempo real" },
    { id: VIEWS.docs, icon: "📚", label: "API Docs", desc: "Referência do endpoint Django" },
  ];

  return (
    <>
      <GlobalStyle />
      <MainContainer>
        <Sidebar />
        <BackgroundPattern />
        <ScanlineEffect />
        <SideContainer>
          <ContentWrapper>

            {/* Header */}
            <HeaderSection>
              <Title>Compilador Solidity</Title>
              <Subtitle>Deploy Web3 sem usar a interface do Remix IDE</Subtitle>
            </HeaderSection>

            {/* Menu */}
            <MenuGrid>
              {menuItems.map(item =>
                activeView === item.id ? (
                  <ActiveButton key={item.id} onClick={() => setActiveView(item.id)}>
                    <ButtonIcon>{item.icon}</ButtonIcon>
                    <ButtonText>{item.label}</ButtonText>
                    <ButtonDescription>{item.desc}</ButtonDescription>
                  </ActiveButton>
                ) : (
                  <ActionButton key={item.id} onClick={() => setActiveView(item.id)}>
                    <ButtonIcon>{item.icon}</ButtonIcon>
                    <ButtonText>{item.label}</ButtonText>
                    <ButtonDescription>{item.desc}</ButtonDescription>
                  </ActionButton>
                )
              )}
            </MenuGrid>

            {/* View: Editor */}
            {activeView === VIEWS.editor && (
              <Panel>
                <PanelTitle>✏️ Solidity Editor — Factorylevel1.sol</PanelTitle>
                <EditorArea
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  spellCheck={false}
                />
              </Panel>
            )}

            {/* View: Workflow */}
            {activeView === VIEWS.workflow && (
              <Panel>
                <PanelTitle>⚙️ Workflow Web3</PanelTitle>

                <SectionLabel>Rede blockchain</SectionLabel>
                <NetworkSelector>
                  {NETWORKS.map(net => (
                    <NetworkBtn
                      key={net.id}
                      active={selectedNetwork === net.id}
                      color={net.color}
                      onClick={() => setSelectedNetwork(net.id)}
                    >
                      {net.label}
                    </NetworkBtn>
                  ))}
                </NetworkSelector>

                <SectionLabel>Conta conectada</SectionLabel>
                <AccountPill onClick={handleSwitchAccount}>
                  <AccountDot connected={!!account} />
                  <AccountText connected={!!account}>
                    {account ? shortAddr(account) : "Nenhuma conta conectada"}
                  </AccountText>
                </AccountPill>

                <SectionLabel style={{ marginTop: "1.25rem" }}>
                  Configurações automáticas 🔒
                </SectionLabel>
                <ConfigCard>
                  <ConfigRow>
                    <ConfigLabel>compilador</ConfigLabel>
                    <ConfigValue><LockIcon>🔒</LockIcon>solc {DEPLOY_CONFIG.compilerVersion}</ConfigValue>
                  </ConfigRow>
                  <ConfigRow>
                    <ConfigLabel>versão EVM</ConfigLabel>
                    <ConfigValue><LockIcon>🔒</LockIcon>{DEPLOY_CONFIG.evmVersion}</ConfigValue>
                  </ConfigRow>
                  <ConfigRow>
                    <ConfigLabel>optimizer</ConfigLabel>
                    <ConfigValue><LockIcon>🔒</LockIcon>ativado · {DEPLOY_CONFIG.optimizer.runs} runs</ConfigValue>
                  </ConfigRow>
                  <ConfigRow>
                    <ConfigLabel>contrato alvo</ConfigLabel>
                    <ConfigValue><LockIcon>🔒</LockIcon>{DEPLOY_CONFIG.contractName}</ConfigValue>
                  </ConfigRow>
                  <ConfigRow>
                    <ConfigLabel>contratos auxiliares</ConfigLabel>
                    <ConfigValue><LockIcon>🔒</LockIcon>SignatureRecovery · contrato_projeto_level1</ConfigValue>
                  </ConfigRow>
                </ConfigCard>

                <WorkflowButtons>
                  <WorkflowBtn
                    className="primary"
                    onClick={handleCompile}
                    disabled={isCompiling}
                  >
                    <StepBadge>01</StepBadge>
                    {isCompiling ? "Compilando…" : "Compilar Contrato"}
                  </WorkflowBtn>

                  <WorkflowBtn className="secondary" onClick={handleConnect}>
                    <StepBadge>02</StepBadge> Conectar MetaMask
                  </WorkflowBtn>

                 <WorkflowBtn
  className="accent"
  onClick={handleDeploy}
  disabled={isDeploying || !compiledAbi || !account}
>
  <StepBadge>03</StepBadge>
  {isDeploying ? "Deployando…" : "Fazer Deploy"}
</WorkflowBtn>

                  <WorkflowBtn className="accent" onClick={handleSigRecovery}>
                    <StepBadge>04</StepBadge> Signature Recovery
                  </WorkflowBtn>

                  {showAccountSelector && (
                    <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--surface-overlay)', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
                      <SectionLabel>Selecione uma conta:</SectionLabel>
                      {accountsList.map(acc => (
                        <NetworkBtn
                          key={acc}
                          active={account === acc}
                          onClick={() => handleSelectAccount(acc)}
                          style={{ marginBottom: '0.5rem', display: 'block', width: '100%', textAlign: 'left' }}
                        >
                          {shortAddr(acc)} {account === acc && '✓'}
                        </NetworkBtn>
                      ))}
                      <NetworkBtn
                        onClick={requestAdditionalAccount}
                        style={{ marginTop: '0.5rem', display: 'block', width: '100%', textAlign: 'left' }}
                      >
                        ➕ Adicionar outra conta...
                      </NetworkBtn>
                    </div>
                  )}
                </WorkflowButtons>

                {factoryAddress && (
                  <Chip style={{ marginTop: "1rem", cursor: "pointer" }}
                    onClick={() => navigator.clipboard?.writeText(factoryAddress)}
                    title="Clique para copiar"
                  >
                    ✅ Factory: {factoryAddress}
                  </Chip>
                )}
              </Panel>
            )}

            {/* View: Console */}
            {activeView === VIEWS.console && (
              <Panel>
                <PanelTitle>🖥️ Console Output</PanelTitle>
                <ConsoleBox>
                  {logs.map((l, i) => (
                    <ConsoleLine key={i} type={l.type}>{l.text}</ConsoleLine>
                  ))}
                  <ConsoleLine type="muted"><Cursor>█</Cursor></ConsoleLine>
                </ConsoleBox>
              </Panel>
            )}

            {/* View: Docs */}
            {activeView === VIEWS.docs && (
              <CodePanel>
                <PanelTitle>📚 Referência do Endpoint Django</PanelTitle>
                <CodeBlock>
                  <code
                    dangerouslySetInnerHTML={{
                      __html: `<span class="cm">// ─── Django API · Contrato de compilação ───────────────────────</span>
                              <span class="cm">// URL:    POST /api/smart_contract_insert/compile (via serviço)</span>
                              <span class="cm">// Header: Content-Type: application/json + Autorização (Bearer)</span>

                              <span class="cm">// ── Request body ────────────────────────────────────────────────</span>
                              {
                                <span class="str">"sources"</span>: {
                                  <span class="str">"Factorylevel1.sol"</span>:            <span class="str">"&lt;código Solidity&gt;"</span>,
                                  <span class="str">"SignatureRecovery.sol"</span>:         <span class="str">"&lt;código Solidity&gt;"</span>,
                                  <span class="str">"contrato_projeto_level1.sol"</span>:  <span class="str">"&lt;código Solidity&gt;"</span>
                                },
                                <span class="str">"compiler_version"</span>: <span class="str">"0.8.24"</span>,
                                <span class="str">"evm_version"</span>:      <span class="str">"cancun"</span>,
                                <span class="str">"optimizer"</span>: { <span class="str">"enabled"</span>: <span class="kw">true</span>, <span class="str">"runs"</span>: 200 },
                                <span class="str">"contract_name"</span>:    <span class="str">"Factorylevel1"</span>
                              }

                              <span class="cm">// ── Response body (sucesso 200) ──────────────────────────────────</span>
                              {
                                <span class="str">"abi"</span>:      [ ... ],         <span class="cm">// ABI JSON array</span>
                                <span class="str">"bytecode"</span>: <span class="str">"0x608060..."</span>,  <span class="cm">// hex string</span>
                                <span class="str">"warnings"</span>: [ ... ]          <span class="cm">// opcional</span>
                              }

                              <span class="cm">// ── Response body (erro 4xx/5xx) ────────────────────────────────</span>
                              {
                                <span class="str">"detail"</span>: <span class="str">"mensagem de erro"</span>   <span class="cm">// ou "error" / "message"</span>
                              }`
                    }}
                  />
                </CodeBlock>
              </CodePanel>
            )}

            {/* Chips */}
            <ChipsRow>
              {[
                `solc ${DEPLOY_CONFIG.compilerVersion}`,
                `EVM: ${DEPLOY_CONFIG.evmVersion}`,
                "optimizer: 200 runs",
                "ethers.js v6",
                "MetaMask",
                "Django REST",
                "SignatureRecovery",
              ].map(c => (
                <Chip key={c}>{c}</Chip>
              ))}
            </ChipsRow>

          </ContentWrapper>
        </SideContainer>
      </MainContainer>
    </>
  );
}